import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  FileParameter,
  Permission,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

import { SUPPORT_CASE_ATTACHMENTS_QUERY_KEY } from './useReachProSupportCaseAttachments';
import { SUPPORT_CASE_NOTES_QUERY_KEY } from './useReachProSupportCaseNotes';
import { SUPPORT_CASES_QUERY_KEY } from './useReachProSupportCases';

type AddReachProSupportCaseNoteVars = {
  workItemId: number;
  messageContent: string;
  attachments: File[];
};

export function useAddReachProSupportCaseNote() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasSupportCreatePermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_Create
  );

  return useMutation({
    mutationFn: async (vars: AddReachProSupportCaseNoteVars) => {
      if (!hasReachProSupportFeature) {
        throw new Error('Support Center feature is not enabled');
      }
      if (!hasSupportCreatePermission) {
        throw new Error('Missing permission to reply to support cases');
      }

      return tryInvokeApi(
        async () => {
          const fileParams: FileParameter[] = vars.attachments.map((f) => ({
            data: f,
            fileName: f.name,
          }));
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).addSupportCaseNote(
            vars.workItemId,
            vars.messageContent,
            fileParams
          );
        },
        (error) => {
          trackError('ReachProSupportCaseClient.addSupportCaseNote', error);
        }
      );
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_CASE_NOTES_QUERY_KEY, vars.workItemId],
      });
      // Refetch attachments so a just-uploaded file renders without a remount
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_CASE_ATTACHMENTS_QUERY_KEY, vars.workItemId],
      });
      // Reorder the inbox and refresh the last-message preview after a reply
      queryClient.invalidateQueries({ queryKey: [SUPPORT_CASES_QUERY_KEY] });
    },
  });
}
